// ═══════════════════════════════════════════════════════════════════
//  modules/erp/PlayerDetail.js
//  PlayerDetail
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { Badge } from "../../composants/Badge";
import { API_BASE } from "../../socle/api";
import { useIsMobile } from "../../socle/hooks";
import { THEME } from "../../socle/theme";
import { PlayerAuditTab } from "./PlayerAuditTab";
import { PlayerBouxTab } from "./PlayerBouxTab";
import { PlayerBoxesTab } from "./PlayerBoxesTab";
import { PlayerGradesTab } from "./PlayerGradesTab";
import { PlayerInventoryTab } from "./PlayerInventoryTab";
import { PlayerSanctionsTab } from "./PlayerSanctionsTab";

export function PlayerDetail({ player, token, onBack, user, onNavigate }) {
  const isMobile = useIsMobile();
  const canAct = !!user && (user.role === "super_admin" || user.role === "admin");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("profil");

  const loadPlayer = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/players/${player.uuid}`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      setData(json);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [player, token]);

  useEffect(() => { loadPlayer(); }, [player.uuid, loadPlayer]);

  const tabs = [
    { id: "profil", label: "👤 Profil" },
    { id: "inventaire", label: "🎒 Inventaire" },
    { id: "grades", label: "🏅 Grades" },
    { id: "boxes", label: "🏠 Boxes" },
    { id: "boux", label: "🦉 Boux" },
    { id: "sanctions", label: "⚖️ Sanctions" },
    { id: "audit", label: "📜 Audit" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={onBack} style={{ background: "transparent", border: `1px solid ${THEME.border}`, color: THEME.textMuted, borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 13 }}>← Retour</button>
        <img src={`https://mc-heads.net/avatar/${player.uuid}/32`} alt={player.username}
          style={{ width: 32, height: 32, imageRendering: "pixelated", borderRadius: 4 }}
          onError={(e) => { e.target.style.display = "none"; }} />
        <h2 style={{ color: THEME.text, fontSize: 20, fontWeight: 800, margin: 0 }}>{player.username}</h2>
        {data && <Badge text={data.online ? "🟢 En ligne" : "⚫ Hors ligne"} bg={data.online ? THEME.emeraldDim : THEME.surface} color={data.online ? THEME.emerald : THEME.textMuted} />}
        <Badge text={player.primary_group} bg={THEME.accentDim} color={THEME.accent} />
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, borderBottom: `1px solid ${THEME.border}`, paddingBottom: 0 }}>
        {tabs.map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{
              background: activeTab === tab.id ? THEME.panel : "transparent",
              border: `1px solid ${activeTab === tab.id ? THEME.border : "transparent"}`,
              borderBottom: activeTab === tab.id ? `1px solid ${THEME.panel}` : `1px solid ${THEME.border}`,
              color: activeTab === tab.id ? THEME.text : THEME.textMuted,
              borderRadius: "6px 6px 0 0", padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: activeTab === tab.id ? 600 : 400,
              marginBottom: -1,
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: "0 8px 8px 8px", padding: 20, minHeight: 300 }}>
        {loading && <div style={{ color: THEME.textMuted }}>Chargement...</div>}

        {!loading && data && activeTab === "profil" && (
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <img src={`https://mc-heads.net/body/${player.uuid}`} alt={player.username}
              style={{ width: 100, imageRendering: "pixelated", alignSelf: "flex-start" }}
              onError={(e) => { e.target.style.display = "none"; }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 10 }}>
                {[
                  ["Pseudo", data.player.username],
                  ["UUID", data.player.uuid],
                  ["Grade principal", data.player.primary_group],
                  ["Statut", data.online ? "🟢 En ligne (Boxed)" : "⚫ Hors ligne"],
                  ["Boxes", `${data.boxes.length} box(es)`],
                ].map(([label, value]) => (
                  <div key={label} style={{ background: THEME.surface, borderRadius: 6, padding: "10px 14px" }}>
                    <div style={{ color: THEME.textMuted, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>{label.toUpperCase()}</div>
                    <div style={{ color: THEME.text, fontSize: 13, wordBreak: "break-all" }}>{value}</div>
                  </div>
                ))}
              </div>
              {data.online && (
                <button onClick={async () => {
                  await fetch(`${API_BASE}/api/players/${player.uuid}/tp`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
                  alert("Téléportation envoyée !");
                }} style={{ background: THEME.accentDim, border: `1px solid ${THEME.accent}`, color: THEME.accent, borderRadius: 6, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 700, alignSelf: "flex-start" }}>
                  🚀 Se téléporter vers ce joueur
                </button>
              )}
            </div>
          </div>
        )}

        {!loading && data && activeTab === "inventaire" && (
          <PlayerInventoryTab uuid={player.uuid} username={player.username} token={token} canAct={canAct} />
        )}

        {!loading && data && activeTab === "grades" && (
          <PlayerGradesTab uuid={player.uuid} groups={data.groups} primaryGroup={data.player.primary_group} token={token} onRefresh={loadPlayer} />
        )}

        {!loading && data && activeTab === "boxes" && (
          <PlayerBoxesTab boxes={data.boxes} />
        )}

        {!loading && data && activeTab === "boux" && (
          <PlayerBouxTab uuid={player.uuid} token={token} />
        )}

        {!loading && data && activeTab === "sanctions" && (
          <PlayerSanctionsTab username={player.username} token={token} onNavigate={onNavigate} />
        )}

        {!loading && data && activeTab === "audit" && (
          <PlayerAuditTab uuid={player.uuid} token={token} />
        )}


      </div>
    </div>
  );
}
